import { productCategories } from "@/data/productCategories";

import { CURRENCY } from "@/data/numbers";
import { cn } from "@/lib/components";
import { formatPrice } from "@/lib/numbers";
import { useState } from "react";
import { Checkbox } from "../base/checkbox";
import { Label } from "../base/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../base/select";
import { Slider } from "../base/slider";

type ProductFiltersValue = {
  categories?: string[];
  minPrice?: number;
  maxPrice?: number;
};

const MIN_PRICE = 0;
const MAX_PRICE = 1000;

export type ProductFiltersProps = {
  value: ProductFiltersValue;
  onChange: (value: ProductFiltersValue) => void;
};

export function ProductFilters(props: ProductFiltersProps) {
  const { value: filtersValue, onChange } = props;

  return (
    <>
      <Label className="whitespace-nowrap">Filter by: </Label>
      <ProductCategoryFilter
        value={filtersValue.categories}
        onChange={(nextCategories) => {
          onChange({
            ...filtersValue,
            categories: nextCategories,
          });
        }}
      />
      <ProductPriceRangeFilter
        minPrice={filtersValue.minPrice}
        maxPrice={filtersValue.maxPrice}
        onChange={(nextPriceRange) => {
          onChange({
            ...filtersValue,
            ...nextPriceRange,
          });
        }}
      />
    </>
  );
}

function ProductPriceRangeFilter(
  props: Pick<ProductFiltersValue, "minPrice" | "maxPrice"> & {
    onChange: (
      value: Pick<ProductFiltersValue, "minPrice" | "maxPrice">,
    ) => void;
  },
) {
  const { minPrice = MIN_PRICE, maxPrice = MAX_PRICE, onChange } = props;
  const [[tmpMinPrice, tmpMaxPrice], setMinPriceAndMaxPrice] = useState([
    minPrice,
    maxPrice,
  ]);

  return (
    <div className="flex gap-2">
      <Label className="text-muted-foreground text-sm">
        {formatPrice(tmpMinPrice, CURRENCY, { digits: 0 })}
      </Label>
      <Slider
        defaultValue={[minPrice, maxPrice]}
        onValueChange={(v) => {
          setMinPriceAndMaxPrice([v[0], v[1]]);
        }}
        onValueCommit={([nextMinPrice, nextMaxPrice]) => {
          onChange({
            minPrice: nextMinPrice,
            maxPrice: nextMaxPrice,
          });
        }}
        min={0}
        max={1000}
        step={10}
        className="w-full"
      />
      <Label className="text-muted-foreground text-sm">
        {formatPrice(tmpMaxPrice, CURRENCY, { digits: 0 })}
      </Label>
    </div>
  );
}

function ProductCategoryFilter(props: {
  value: ProductFiltersValue["categories"];
  onChange: (value: ProductFiltersValue["categories"]) => void;
}) {
  const { value = [], onChange } = props;
  return (
    <Select>
      <SelectTrigger className="w-full">
        <SelectValue
          placeholder={
            value.length === 0
              ? "Select a category"
              : value
                  .map(
                    (cat) =>
                      productCategories.find((pc) => pc.value === cat)!.label,
                  )
                  .join(", ")
          }
        />
      </SelectTrigger>
      <SelectContent>
        {productCategories.map((productCategory) => {
          const isSelected = value.includes(productCategory.value);
          return (
            <SelectItem
              key={productCategory.path}
              className={cn("hover:bg-input/50 flex items-center gap-2 p-2", {
                "bg-input/50": value.includes(productCategory.value),
              })}
              value={productCategory.value}
              onPointerUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(
                  !isSelected
                    ? [...value, productCategory.value]
                    : value.filter((cat) => cat !== productCategory.value),
                );
              }}
            >
              <Checkbox
                id={`category-${productCategory.value}`}
                checked={isSelected}
              />
              <Label
                htmlFor={`category-${productCategory.value}`}
                className="flex-1 cursor-pointer text-sm font-normal"
              >
                {productCategory.label}
              </Label>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
